import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, EyeOff, Info, CheckCircle, AlertTriangle,
  AlertOctagon, Plus, Trash2, ArrowUp, ArrowDown, Pencil, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const BANNER_TYPES = [
  { value: 'info', label: 'Info', icon: Info, color: 'sky' },
  { value: 'success', label: 'Success', icon: CheckCircle, color: 'emerald' },
  { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'amber' },
  { value: 'urgent', label: 'Urgent', icon: AlertOctagon, color: 'red' },
];

const DEFAULT_BANNER = {
  is_active: false,
  title: '',
  message: '',
  banner_type: 'info',
  cta_label: '',
  cta_url: '',
  starts_at: '',
  ends_at: '',
  version: '1',
};

function BannerForm({ label, banner, onChange, onSave, saving }) {
  const isActive = banner.is_active;

  return (
    <div className="card" data-testid={`banner-form-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">{label} Dashboard Banner</h3>
        <button
          onClick={() => onChange({ ...banner, is_active: !isActive })}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            isActive
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-slate-100 text-slate-500'
          }`}
          data-testid={`toggle-${label.toLowerCase()}`}
        >
          {isActive ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      <div className="space-y-4">
        {/* Banner Type */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Banner Type</label>
          <div className="flex gap-2" data-testid={`type-selector-${label.toLowerCase()}`}>
            {BANNER_TYPES.map((t) => {
              const Icon = t.icon;
              const selected = banner.banner_type === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => onChange({ ...banner, banner_type: t.value })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    selected
                      ? `bg-${t.color}-100 border-${t.color}-300 text-${t.color}-700`
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
          <input
            type="text"
            value={banner.title}
            onChange={(e) => onChange({ ...banner, title: e.target.value })}
            placeholder="e.g. New Feature Available"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            data-testid={`title-${label.toLowerCase()}`}
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
          <textarea
            value={banner.message}
            onChange={(e) => onChange({ ...banner, message: e.target.value })}
            placeholder="Write a short, helpful notice for this dashboard..."
            rows={2}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            data-testid={`message-${label.toLowerCase()}`}
          />
        </div>

        {/* CTA */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">CTA Label <span className="text-slate-400">(optional)</span></label>
            <input
              type="text"
              value={banner.cta_label || ''}
              onChange={(e) => onChange({ ...banner, cta_label: e.target.value })}
              placeholder="Learn More"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid={`cta-label-${label.toLowerCase()}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">CTA URL <span className="text-slate-400">(optional)</span></label>
            <input
              type="text"
              value={banner.cta_url || ''}
              onChange={(e) => onChange({ ...banner, cta_url: e.target.value })}
              placeholder="/plans or https://..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid={`cta-url-${label.toLowerCase()}`}
            />
          </div>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date <span className="text-slate-400">(optional)</span></label>
            <input
              type="datetime-local"
              value={banner.starts_at ? banner.starts_at.slice(0, 16) : ''}
              onChange={(e) => onChange({ ...banner, starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid={`starts-at-${label.toLowerCase()}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date <span className="text-slate-400">(optional)</span></label>
            <input
              type="datetime-local"
              value={banner.ends_at ? banner.ends_at.slice(0, 16) : ''}
              onChange={(e) => onChange({ ...banner, ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid={`ends-at-${label.toLowerCase()}`}
            />
          </div>
        </div>

        {/* Version */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Version <span className="text-slate-400">(increment to reset dismissals)</span>
          </label>
          <input
            type="text"
            value={banner.version}
            onChange={(e) => onChange({ ...banner, version: e.target.value })}
            placeholder="1"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 max-w-[200px]"
            data-testid={`version-${label.toLowerCase()}`}
          />
        </div>

        {/* Save */}
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          data-testid={`save-${label.toLowerCase()}`}
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : `Save ${label} Banner`}
        </button>
      </div>
    </div>
  );
}

export default function AdminBannerManager() {
  const navigate = useNavigate();
  const [tradesBanner, setTradesBanner] = useState(DEFAULT_BANNER);
  const [employerBanner, setEmployerBanner] = useState(DEFAULT_BANNER);
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBanners = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/dashboard-banners`, { withCredentials: true });
      setTradesBanner({ ...DEFAULT_BANNER, ...data.tradesperson });
      setEmployerBanner({ ...DEFAULT_BANNER, ...data.employer });
    } catch (err) {
      toast.error('Failed to load banner settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const saveBanner = async (target) => {
    setSaving(target);
    const body = target === 'tradesperson' ? tradesBanner : employerBanner;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/dashboard-banners/${target}`, body, { withCredentials: true });
      toast.success(`${target === 'tradesperson' ? 'Tradesperson' : 'Employer'} banner saved`);
    } catch (err) {
      // Surface the backend detail (e.g. retired-banner blocklist)
      // instead of a generic 'Failed to save' — otherwise admins
      // retry the same rejected payload endlessly.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : `Failed to save ${target} banner`);
      // Revert optimistic local state so the pill can never show
      // 'Active' while the persisted doc stayed inactive.
      await fetchBanners();
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-banner-manager">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Dashboard Banners</h1>
            <p className="text-sm text-slate-500">Manage notices shown on Tradesperson and Employer dashboards</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <BannerForm
          label="Tradesperson"
          banner={tradesBanner}
          onChange={setTradesBanner}
          onSave={() => saveBanner('tradesperson')}
          saving={saving === 'tradesperson'}
        />

        <BannerForm
          label="Employer"
          banner={employerBanner}
          onChange={setEmployerBanner}
          onSave={() => saveBanner('employer')}
          saving={saving === 'employer'}
        />

        <PromoMessagesSection />
      </div>
    </div>
  );
}


/**
 * PromoMessagesSection — admin CRUD for the rotating site-wide promo
 * banner. Reuses the existing banner collection, the admin auth cookie
 * and the same colour taxonomy as the dashboard banners. Changes are
 * saved directly to Mongo and reflected on the next page view (no
 * redeploy needed).
 */
function PromoMessagesSection() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ text: '', cta_label: '', cta_url: '', banner_type: 'info', is_active: true });

  const resetForm = () => setForm({ text: '', cta_label: '', cta_url: '', banner_type: 'info', is_active: true });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/dashboard-banners/promo/messages`, { withCredentials: true });
      setMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch {
      toast.error('Failed to load promo messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    if (!form.text.trim()) { toast.error('Message text is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/dashboard-banners/promo/messages/${editingId}`, form, { withCredentials: true });
        toast.success('Message updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/dashboard-banners/promo/messages`, form, { withCredentials: true });
        toast.success('Message added');
      }
      setEditingId(null);
      resetForm();
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this promo message?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/dashboard-banners/promo/messages/${id}`, { withCredentials: true });
      toast.success('Deleted');
      await refresh();
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggle = async (m) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/dashboard-banners/promo/messages/${m.id}`,
        { ...m, is_active: !m.is_active }, { withCredentials: true });
      await refresh();
    } catch { toast.error('Toggle failed'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= messages.length) return;
    const ids = messages.map((m) => m.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    try {
      await axios.put(`${BACKEND_URL}/api/admin/dashboard-banners/promo/reorder`, { ids }, { withCredentials: true });
      await refresh();
    } catch { toast.error('Reorder failed'); }
  };

  const beginEdit = (m) => {
    setEditingId(m.id);
    setForm({
      text: m.text || '',
      cta_label: m.cta_label || '',
      cta_url: m.cta_url || '',
      banner_type: m.banner_type || 'info',
      is_active: !!m.is_active,
    });
  };

  const cancelEdit = () => { setEditingId(null); resetForm(); };

  return (
    <div className="card" data-testid="promo-messages-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Promo Banner Messages</h3>
          <p className="text-sm text-slate-500">Rotating strip under the top nav. Shown on every public page. Rotates every 10s.</p>
        </div>
      </div>

      {/* Add / Edit form */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 space-y-3" data-testid="promo-message-form">
        <input
          type="text"
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          placeholder="Message text (e.g. New Carpenters now available in Surrey)"
          maxLength={200}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
          data-testid="promo-message-text"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text" value={form.cta_label}
            onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
            placeholder="CTA label (optional)" maxLength={32}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            data-testid="promo-message-cta-label"
          />
          <input
            type="text" value={form.cta_url}
            onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
            placeholder="CTA URL (optional) — /search or https://..."
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            data-testid="promo-message-cta-url"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {['info','success','warning','urgent'].map((t) => (
            <button key={t} type="button"
              onClick={() => setForm({ ...form, banner_type: t })}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border ${form.banner_type===t?'bg-orange-500 text-white border-orange-500':'bg-white text-slate-700 border-slate-300'}`}
              data-testid={`promo-type-${t}`}
            >{t}</button>
          ))}
          <label className="flex items-center gap-1 text-sm text-slate-700 ml-auto">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="promo-message-active" />
            Active
          </label>
          <button type="button" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-sm font-medium disabled:opacity-50"
            data-testid="promo-message-save">
            {editingId ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add message'}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md text-sm font-medium"
              data-testid="promo-message-cancel-edit">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : messages.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-6 text-center" data-testid="promo-messages-empty">
          No promo messages yet. The banner will stay hidden until you add one.
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg" data-testid="promo-messages-list">
          {messages.map((m, i) => (
            <li key={m.id} className="px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`promo-message-${m.id}`}>
              <span className={`inline-flex items-center text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                m.banner_type === 'success' ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                : m.banner_type === 'warning' ? 'border-amber-300 text-amber-700 bg-amber-50'
                : m.banner_type === 'urgent'  ? 'border-red-300 text-red-700 bg-red-50'
                : 'border-sky-300 text-sky-700 bg-sky-50'
              }`}>{m.banner_type}</span>
              <span className="flex-1 min-w-0 text-sm text-slate-900 truncate" title={m.text}>{m.text}</span>
              {m.cta_label && (
                <span className="text-xs text-slate-500 truncate">→ {m.cta_label}</span>
              )}
              <button type="button" onClick={() => toggle(m)}
                className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md ${m.is_active?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}
                data-testid={`promo-toggle-${m.id}`}>
                {m.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {m.is_active ? 'On' : 'Off'}
              </button>
              <button type="button" onClick={() => move(i, -1)} disabled={i===0}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                data-testid={`promo-up-${m.id}`} aria-label="Move up">
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i===messages.length-1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                data-testid={`promo-down-${m.id}`} aria-label="Move down">
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => beginEdit(m)}
                className="p-1 rounded hover:bg-slate-100 text-slate-600"
                data-testid={`promo-edit-${m.id}`} aria-label="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => remove(m.id)}
                className="p-1 rounded hover:bg-red-50 text-red-600"
                data-testid={`promo-delete-${m.id}`} aria-label="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
